import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AutoAwesome,
  BoltOutlined,
  CheckCircleOutline,
  ExpandMore,
  LockOutlined,
  SmartToyOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import Header from "../components/Header";
import FooterComp from "../components/FooterComp";
import Pricing from "../components/Pricing";
import { GlobalContext } from "../../context/GlobalContext";

const valueCards = [
  {
    icon: AutoAwesome,
    title: "One creative AI workspace",
    text: "Create images, videos, AI characters, ads, music and clips without juggling multiple tools.",
  },
  {
    icon: BoltOutlined,
    title: "Credits that follow your workflow",
    text: "Use one credit balance across the studio and choose the models that fit each project.",
  },
  {
    icon: TrendingUpOutlined,
    title: "Built to help you earn",
    text: "Produce content for clients, campaigns and your own brand from one streamlined workspace.",
  },
];

const faqs = [
  [
    "What are AI credits?",
    "Credits are the balance used when you run generation tasks. Different models and workflows may use different amounts, so you stay in control of how you spend them.",
  ],
  [
    "Which plan should I choose?",
    "Choose based on how often you create and the types of projects you run. For a tailored recommendation, open Beyund Assistant and describe your expected workflow.",
  ],
  [
    "Can I buy another plan later?",
    "Yes. You can purchase another paid plan when you need more credits. New plan credits are added to your available balance.",
  ],
  [
    "Do unused credits expire?",
    "No. Purchased credits remain in your balance until you use them, so you can create at your own pace.",
  ],
  [
    "How do I access the studio after payment?",
    "After payment is verified, the thank-you page confirms your purchase and automatically signs you into the app.",
  ],
];

const PricingPage = () => {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const [web, setWeb] = React.useState({});

  React.useEffect(() => {
    (async () => {
      const res = await hitAxios({
        path: "/api/web/get_web_public",
        post: false,
        admin: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success) setWeb(res.data.data || {});
    })();
  }, [hitAxios]);

  const openCloser = () => window.dispatchEvent(new Event("openLeadCloser"));
  const scrollToPlans = () =>
    document.getElementById("pricing-plans")?.scrollIntoView({ behavior: "smooth" });

  return (
    <Box
      onContextMenu={(event) => event.stopPropagation()}
      sx={{
        minHeight: "auto",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      <Header web={web} />
      <Box sx={{ height: { xs: 56, md: 64 } }} />
      <Box component="main">
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            py: { xs: 7, md: 10 },
            background: `radial-gradient(circle at 20% 20%, ${alpha(
              theme.palette.primary.main,
              0.2,
            )}, transparent 38%), radial-gradient(circle at 80% 10%, ${alpha(
              theme.palette.secondary.main,
              0.15,
            )}, transparent 36%)`,
          }}
        >
          <Container maxWidth="md">
            <Stack alignItems="center" textAlign="center" spacing={3}>
              <Chip icon={<AutoAwesome />} label="Paid plans built for creators" color="primary" />
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "4.5rem" },
                  fontWeight: 900,
                  lineHeight: 1.02,
                  letterSpacing: "-0.045em",
                }}
              >
                Turn ideas into content that{" "}
                <Box component="span" sx={{ color: "primary.main" }}>
                  grows your business.
                </Box>
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ maxWidth: 700, fontSize: { xs: "1rem", md: "1.2rem" }, lineHeight: 1.75 }}
              >
                Choose a plan, unlock the complete Beyund AI studio, and use one credit balance
                across powerful image, video, character, music and marketing workflows.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button size="large" variant="contained" onClick={scrollToPlans}>
                  Compare plans
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<SmartToyOutlined />}
                  onClick={openCloser}
                >
                  Ask AI which plan fits
                </Button>
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 3 }}
                color="text.secondary"
              >
                {["Secure checkout", "No free or trial tier", "Instant access after payment"].map(
                  (item) => (
                    <Stack key={item} direction="row" spacing={0.7} alignItems="center">
                      <CheckCircleOutline color="success" fontSize="small" />
                      <Typography variant="body2">{item}</Typography>
                    </Stack>
                  ),
                )}
              </Stack>
            </Stack>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ mt: { xs: -3, md: -5 }, position: "relative", zIndex: 1 }}>
          <Grid container spacing={2}>
            {valueCards.map(({ icon: Icon, title, text }) => (
              <Grid item xs={12} md={4} key={title}>
                <Box
                  sx={{
                    height: "100%",
                    p: 3,
                    borderRadius: 3,
                    bgcolor: "background.paper",
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <Icon color="primary" />
                  <Typography fontWeight={800} mt={1.5} mb={0.8}>
                    {title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                    {text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Box id="pricing-plans" sx={{ scrollMarginTop: 80 }}>
          <Pricing showHeader={false} />
        </Box>

        <Box sx={{ py: { xs: 5, md: 7 }, bgcolor: alpha(theme.palette.primary.main, 0.035) }}>
          <Container maxWidth="lg">
            <Typography variant="overline" color="primary" fontWeight={800}>
              Simple from payment to production
            </Typography>
            <Typography variant="h3" fontWeight={900} mt={1} mb={4}>
              Start creating in three steps
            </Typography>
            <Grid container spacing={3}>
              {[
                ["01", "Choose your plan", "Compare credits and AI character capacity. Your unused credits never expire."],
                ["02", "Pay securely", "Create your account and complete payment using an available gateway."],
                ["03", "Enter the studio", "Your payment is verified and the app signs you in automatically."],
              ].map(([number, title, text]) => (
                <Grid item xs={12} md={4} key={number}>
                  <Typography color="primary" fontWeight={900} fontSize="2rem">
                    {number}
                  </Typography>
                  <Typography variant="h6" fontWeight={800} my={1}>
                    {title}
                  </Typography>
                  <Typography color="text.secondary" lineHeight={1.7}>
                    {text}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 }, pb: { xs: 4, md: 5 } }}>
          <Stack alignItems="center" textAlign="center" mb={3}>
            <LockOutlined color="primary" />
            <Typography variant="h3" fontWeight={900} mt={1.5}>
              Questions before you choose?
            </Typography>
            <Typography color="text.secondary" mt={1}>
              Get quick answers here or ask Beyund Assistant for a recommendation.
            </Typography>
          </Stack>
          {faqs.map(([question, answer]) => (
            <Accordion key={question} disableGutters elevation={0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={700}>{question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary" lineHeight={1.7}>
                  {answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
          <Stack alignItems="center" mt={3} mb={1}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SmartToyOutlined />}
              onClick={openCloser}
            >
              Talk to Beyund Assistant
            </Button>
          </Stack>
        </Container>
      </Box>

      <FooterComp web={web} />
    </Box>
  );
};

export default PricingPage;
